import React, { useContext, useEffect, useState } from 'react'
import './MyOrders.css'
import axios from 'axios'
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../Context/StoreContext';
import { assets } from '../../assets/assets';
import { toast } from 'react-toastify';
import OrderTracking from '../../components/OrderTracking/OrderTracking';
import OrderQRCode from '../../components/OrderQRCode/OrderQRCode';

// Use backend role-based status mapping
const getCustomerStatusDisplay = async (backendStatus) => {
  try {
    // Since we can't import ES modules directly, we'll use the mapping inline
    // This should match the backend utils/orderStatusUtils.js
    const statusMap = {
      'Created': 'Created',
      'Paid': 'Created', 
      'Restaurant Confirmed': 'Restaurant Confirmed',
      'Driver on the way to Restaurant': 'Driver on the way to Restaurant',
      'Food Processing': 'Food Processing',
      'Ready': 'Ready',
      'Order picked up by driver': 'Order picked up by driver',
      'Out for Delivery': 'Driver coming with your order',
      'Delivered': 'Delivered',
      'Completed': 'Delivered',
      'Cancelled': 'Cancelled',
      'Refunded': 'Cancelled',
      'Failed': 'Failed'
    };
    
    return statusMap[backendStatus] || backendStatus;
  } catch (error) {
    return backendStatus;
  }
};

// Map backend status to customer-friendly display status
const mapStatusToCustomerView = (backendStatus, deliveryType = 'delivery') => {
  if (deliveryType === 'pickup') {
    const pickupStatusMap = {
      'Created': 'Order Placed',
      'Paid': 'Order Placed', 
      'Restaurant Confirmed': 'Restaurant Confirmed',
      'Food Processing': 'Food Processing',
      'Ready for Pickup': 'Ready for Pickup',
      'Picked Up': 'Picked Up',
      'Cancelled': 'Cancelled'
    };
    return pickupStatusMap[backendStatus] || backendStatus;
  }
  
  // Delivery order statuses
  const deliveryStatusMap = {
    'Created': 'Created',
    'Paid': 'Created',
    'Restaurant Confirmed': 'Restaurant Confirmed',
    'Driver on the way to Restaurant': 'Driver on the way to Restaurant',
    'Food Processing': 'Food Processing',
    'Ready': 'Ready',
    'Order picked up by driver': 'Order picked up by driver',
    'Out for Delivery': 'Driver coming with your order',
    'Delivered': 'Delivered',
    'Completed': 'Delivered',
    'Cancelled': 'Cancelled',
    'Refunded': 'Cancelled',
    'Failed': 'Failed'
  };
  
  return deliveryStatusMap[backendStatus] || backendStatus;
};

// Map customer-friendly status to backend status for filtering
const mapCustomerStatusToBackend = (customerStatus) => {
  const statusMap = {
    'Created': 'Created',
    'Restaurant Confirmed': 'Restaurant Confirmed',
    'Driver on the way to Restaurant': 'Driver on the way to Restaurant',
    'Food Processing': 'Food Processing',
    'Ready': 'Ready',
    'Order picked up by driver': 'Order picked up by driver',
    'Driver coming with your order': 'Out for Delivery',
    'Delivered': 'Delivered',
    'Cancelled': 'Cancelled'
  };
  
  return statusMap[customerStatus] || customerStatus;
};

const MyOrders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { url, token, currency, updateNotificationCount } = useContext(StoreContext);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [autoRefresh] = useState(true);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'All',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  // Pagination state
  const PAGE_SIZE = 2;
  const [currentPage, setCurrentPage] = useState(1);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      
      // Map frontend-friendly status values to backend-expected values for validation
      let backendStatus;
      if (filters.status !== 'All') {
        // Map to values exactly as accepted by the validation schema
        const statusMap = {
          'Pending': 'pending',
          'Processing': 'confirmed', 
          'Food Processing': 'preparing',
          'Ready for Pickup': 'ready_for_pickup',
          'Out for delivery': 'out_for_delivery',
          'Delivered': 'delivered',
          'Cancelled': 'cancelled'
        };
        backendStatus = statusMap[filters.status] || undefined;
      }
      
      // Use POST with body parameters (backend expects body params as per route definition)
      const response = await axios.post(
        `${url}/api/order/userorders`,
        {
          status: backendStatus,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          limit: 10,
          page: 1
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}` 
          } 
        }
      );
      
      // Correctly access the orders array from the response
      const orderData = response.data.data?.orders || [];
      
      // Debug restaurant data structure
      if (orderData.length > 0) {
        console.log('Sample order restaurant data:', orderData[0].restaurant);
      }
      
      setOrders(Array.isArray(orderData) ? orderData : []);
      
      // Show success message when filters are applied and return results
      if (filters.status !== 'All' || filters.startDate || filters.endDate) {
        const filterMessage = `Found ${orderData.length} order${orderData.length !== 1 ? 's' : ''} matching your filters`;
        toast.success(filterMessage);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load your orders");
      setOrders([]); // Set orders to empty array on error
      setLoading(false);
    }
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Toggle order details visibility
  const toggleOrderDetails = (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
    }
  };
  
  // Open tracking modal
  const openTracking = (orderId, e) => {
    e.stopPropagation(); // Prevent triggering the toggleOrderDetails
    setTrackingOrder(orderId);
  };
  
  // Close tracking modal
  const closeTracking = () => {
    setTrackingOrder(null);
  };
  
  // Open cancellation modal
  const openCancellation = (orderId, e) => {
    e.stopPropagation(); // Prevent triggering the toggleOrderDetails
    setCancellingOrder(orderId);
    setCancellationReason('');
  };
  
  // Close cancellation modal
  const closeCancellation = () => {
    setCancellingOrder(null);
    setCancellationReason('');
  };
  
  // Check if order can be cancelled
  const canBeCancelled = (order) => {
    const status = order.status || 'Created';
    return ['Created', 'Paid', 'Restaurant Confirmed'].includes(status);
  };
  
  // Handle order cancellation
  const handleCancelOrder = async () => {
    if (!cancellingOrder) return;
    
    try {
      setCancellationLoading(true);
      
      const response = await axios.post(
        `${url}/api/order/cancel`,
        { 
          orderId: cancellingOrder,
          reason: cancellationReason.trim() || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Order cancelled successfully');
        closeCancellation();
        
        // Update notification count to reflect cancelled order
        updateNotificationCount(prev => Math.max(0, prev - 1));
        
        fetchOrders();
        
        if (response.data.needsRefund) {
          toast.info('Your payment will be refunded within 3-5 business days');
        }
      } else {
        toast.error(`Failed to cancel order: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      const errorMsg = error.response?.data?.message || 'Failed to cancel order';
      toast.error(errorMsg);
    } finally {
      setCancellationLoading(false);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // Apply the filters
  const applyFilters = (e) => {
    e.preventDefault();
    fetchOrders();
    // If on mobile, hide filter panel after applying
    if (window.innerWidth < 768) {
      setShowFilters(false);
    }
  };
  
  // Reset filters to default
  const resetFilters = () => {
    setFilters({
      status: 'All',
      startDate: '',
      endDate: ''
    });
    // Don't call fetchOrders here as useEffect will handle it when filters change
  };
  
  // Toggle filters panel visibility (for mobile)
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  // Set up auto-refresh for ongoing orders
  useEffect(() => {
    if (token) {
      fetchOrders();
      
      // Check if any order is in a non-final state
      const hasActiveOrders = Array.isArray(orders) && orders.some(order => {
        // For pickup orders, "Picked Up" is the final status
        if (order.deliveryType === 'pickup') {
          return order.status !== 'Picked Up' && 
                 order.status !== 'Cancelled' && 
                 order.status !== 'Failed';
        }
        // For delivery orders, use standard logic
        return order.status !== 'Delivered' && 
               order.status !== 'Cancelled' && 
               order.status !== 'Failed';
      });
      
      // Only auto-refresh if there are active orders and user hasn't disabled it
      if (hasActiveOrders && autoRefresh) {
        const interval = setInterval(fetchOrders, 60000); // Refresh every minute
        return () => clearInterval(interval);
      }
    }
  }, [token, orders.length, autoRefresh]);

  // Fetch orders when filters change
  useEffect(() => {
    if (token) {
      fetchOrders();
    }
  }, [filters, token]);

  // Clamp page when orders change
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
    setCurrentPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [orders]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading && orders.length === 0) {
    return (
      <div className="my-orders">
        <h2>{t('myOrders.title')}</h2>
        <div className="loading">{t('myOrders.loading')}</div>
      </div>
    );
  }

  return (
    <div className='my-orders'>
      <div className="my-orders-header">
        <h2>{t('myOrders.title')}</h2>
        
        <div className="header-controls">
          <button className="refresh-button" onClick={fetchOrders}>
            {t('myOrders.refresh')}
          </button>
          
          <button 
            className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
            onClick={toggleFilters}
          >
            {showFilters ? t('myOrders.hide_filters') : t('myOrders.show_filters')}
          </button>
        </div>
      </div>
      
      <div className="order-controls">
        
        {showFilters && (
          <div className="order-filters">
            <form onSubmit={applyFilters} className="filter-form">
              <div className="filter-group">
                <label htmlFor="status">{t('myOrders.status')}:</label>
                <select 
                  id="status" 
                  name="status" 
                  value={filters.status} 
                  onChange={handleFilterChange}
                >
                  <option value="All">{t('myOrders.all_statuses')}</option>
                  <option value="Created">Created</option>
                  <option value="Restaurant Confirmed">Restaurant Confirmed</option>
                  <option value="Driver on the way to Restaurant">Driver on the way to Restaurant</option>
                  <option value="Food Processing">Food Processing</option>
                  <option value="Ready">Ready</option>
                  <option value="Order picked up by driver">Order picked up by driver</option>
                  <option value="Driver coming with your order">Driver coming with your order</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Ready for Pickup">Ready for Pickup</option>
                  <option value="Picked Up">Picked Up</option>
                  <option value="Pickup Completed">Pickup Completed</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="startDate">{t('myOrders.from_date')}:</label>
                <input 
                  type="date" 
                  id="startDate" 
                  name="startDate" 
                  value={filters.startDate} 
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="filter-group">
                <label htmlFor="endDate">{t('myOrders.to_date')}:</label>
                <input 
                  type="date" 
                  id="endDate" 
                  name="endDate" 
                  value={filters.endDate} 
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="filter-actions">
                <button type="submit" className="filter-apply">{t('myOrders.apply')}</button>
                <button type="button" onClick={resetFilters} className="filter-reset">{t('myOrders.reset')}</button>
              </div>
            </form>
          </div>
        )}
      </div>
      
      {!Array.isArray(orders) ? (
        <div className="no-orders">
          <p>{t('myOrders.unable_to_load')}</p>
          <button onClick={fetchOrders} className="retry-button">{t('myOrders.retry')}</button>
        </div>
      ) : orders.length === 0 ? (
        <div className="no-orders">
          <div className="no-orders-content">
            <div className="no-orders-icon">📋</div>
            <h3>{t('myOrders.no_orders_found')}</h3>
            {filters.status !== 'All' || filters.startDate || filters.endDate ? (
              <>
                <p>{t('myOrders.no_orders_match_filters')}</p>
                <button onClick={resetFilters} className="clear-filters-btn">{t('myOrders.clear_filters')}</button>
              </>
            ) : (
              <>
                <p>{t('myOrders.no_orders_yet')}</p>
                <p className="no-orders-subtitle">{t('myOrders.ready_to_order')}</p>
                <button onClick={() => window.location.href = '/'} className="browse-restaurants-btn">
                  {t('myOrders.browse_restaurants')}
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="container">
          {orders
            .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
            .map((order) => (
            <div key={order._id} className='my-orders-card'>
              <div className='my-orders-header' onClick={() => toggleOrderDetails(order._id)}>
                <div className="order-icon">
                  <img src={assets.parcel_icon} alt="Order" />
                </div>
                <div className="order-summary">
                  <p className="order-id">{t('myOrders.order_number')}{order._id.substring(order._id.length - 6)}</p>
                  <p className="order-date">{formatDate(order.date || order.createdAt || order.placedAt)}</p>
                </div>
                <div className="order-amount">
                  <p>{Math.round(order.amount || order.total || 0)} {currency}</p>
                </div>
                <div className="order-status">
                  <span className={`status-dot ${mapStatusToCustomerView(order.status || 'Created', order.deliveryType).toLowerCase().replace(/\s+/g, '-')}`}>&#x25cf;</span>
                  <p className={mapStatusToCustomerView(order.status || 'Created', order.deliveryType).toLowerCase().replace(/\s+/g, '-')}>
                    {mapStatusToCustomerView(order.status || 'Created', order.deliveryType)}
                  </p>
                  {/* Pickup/Delivery indicator */}
                  {order.deliveryType && (
                    <span className={`delivery-type-indicator ${order.deliveryType === 'pickup' ? 'pickup' : 'delivery'}`}>
                      {order.deliveryType === 'pickup' ? '🏪 Pickup' : '🚚 Delivery'}
                    </span>
                  )}
                </div>
                <div className="order-actions-header">
                  <button 
                    className="track-button" 
                    onClick={(e) => openTracking(order._id, e)}
                    title={t('myOrders.track_order')}
                  >
                    {t('myOrders.track')}
                  </button>
                  
                  {canBeCancelled(order) && (
                    <button 
                      className="cancel-button" 
                      onClick={(e) => openCancellation(order._id, e)}
                      title={t('myOrders.cancel_order')}
                    >
                      {t('myOrders.cancel')}
                    </button>
                  )}
                </div>
                <div className="expand-icon">
                  {expandedOrder === order._id ? '▲' : '▼'}
                </div>
              </div>
              
              {expandedOrder === order._id && (
                <div className="order-details">
                  <div className="order-items">
                    <h4>{t('myOrders.items')}</h4>
                    <ul>
                      {(order.items || []).map((item, index) => (
                        <li key={index} className="order-item">
                          <div className="item-name">{item.name}</div>
                          <div className="item-quantity">x{item.quantity}</div>
                          <div className="item-price">{Math.round(item.price * item.quantity)} {currency}</div>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Detailed pricing breakdown */}
                    <div className="order-pricing">
                      <div className="pricing-row">
                        <span>{t('myOrders.subtotal')}:</span>
                        <span>{Math.round(order.subtotal || 0)} {currency}</span>
                      </div>

                      <div className="pricing-row">
                        <span>{order.deliveryType === 'pickup' ? t('myOrders.pickup_fee') : t('myOrders.delivery_fee')}:</span>
                        <span>{Math.round(order.deliveryFee || 0)} {currency}</span>
                      </div>
                      <div className="pricing-row total">
                        <span>{t('myOrders.total')}:</span>
                        <span>{Math.round(order.total || order.amount || 0)} {currency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="order-addresses">
                    {order.deliveryType === 'delivery' ? (
                      <div className="delivery-address">
                        <h4>{t('myOrders.delivery_address')}</h4>
                        {(order.address.firstName || order.address.lastName) && (
                          <p><strong>{t('myOrders.name')}:</strong> {order.address.firstName || ''} {order.address.lastName || ''}</p>
                        )}
                        <p className="address-line">
                          <strong>{t('myOrders.address')}:</strong> {[
                            order.address.street,
                            order.address.quartier,
                            order.address.city,
                            order.address.state,
                            order.address.country,
                            order.address.zipCode || order.address.zipcode
                          ].filter(Boolean).join(', ')}
                        </p>
                        <p><strong>{t('myOrders.phone')}:</strong> {order.address.phone || order.contactPhone || t('myOrders.not_provided')}</p>
                      </div>
                    ) : (
                      <div className="pickup-info">
                        <h4>🏪 {t('myOrders.pickup_information')}</h4>
                        <p><strong>{t('myOrders.pickup_location')}:</strong> {t('myOrders.restaurant')}</p>
                        <p><strong>{t('myOrders.pickup_instructions')}</strong></p>
                        <p><strong>{t('myOrders.phone')}:</strong> {order.contactPhone || t('myOrders.not_provided')}</p>
                      </div>
                    )}
                    
                    {order.restaurant && (
                      <div className="restaurant-address">
                        <h4>{t('myOrders.restaurant')}</h4>
                        <p><strong>{t('myOrders.restaurant_name')}:</strong> {order.restaurant.name || t('myOrders.restaurant')}</p>
                        <p className="address-line">
                          <strong>{t('myOrders.restaurant_address')}:</strong> {
                            order.restaurant.address ? (
                              typeof order.restaurant.address === 'object' 
                                ? [
                                    order.restaurant.address.street,
                                    order.restaurant.address.quartier,
                                    order.restaurant.address.city,
                                    order.restaurant.address.country,
                                    order.restaurant.address.zipCode
                                  ].filter(Boolean).join(', ')
                                : order.restaurant.address
                            ) : t('myOrders.address_not_available')
                          }
                        </p>
                        <p><strong>{t('myOrders.phone')}:</strong> {order.restaurant.contactInfo?.phone || order.restaurant.phone || t('myOrders.not_available')}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="order-payment">
                    <h4>{t('myOrders.payment')}</h4>
                    <div className="payment-status-display">
                      <div className="payment-row">
                        <span>{t('myOrders.payment_status')}:</span>
                        <span className={`payment-status ${order.paymentStatus?.toLowerCase() || 'pending'}`}>
                          {order.paymentStatus || t('myOrders.pending')}
                        </span>
                      </div>
                      <div className="payment-row">
                        <span>{t('myOrders.payment_method')}:</span>
                        <span className="payment-method">{order.paymentMethod || (order.payment ? t('myOrders.online_payment') : t('myOrders.cash_on_delivery'))}</span>
                      </div>
                      {order.paymentMethod === 'Cash on Delivery' && order.paymentStatus === 'Pending'}
                    </div>
                  </div>

                  {/* QR Code for pickup orders - show only for active pickup orders */}
                  {order.deliveryType === 'pickup' && 
                   !['Picked Up', 'Cancelled', 'Failed'].includes(mapStatusToCustomerView(order.status || 'Created', order.deliveryType)) && (
                    <div className="order-qr-section">
                      <OrderQRCode 
                        orderId={order._id} 
                        size="medium"
                        showTitle={true}
                      />
                    </div>
                  )}

                  {/* Delivery Photo for delivered orders */}
                  {order.deliveryPhoto && (order.status === 'Delivered' || order.status === 'Completed') && (
                    <div className="delivery-photo-section">
                      <h4>📸 {t('myOrders.delivery_confirmation')}</h4>
                      <div className="delivery-photo-container">
                        <img 
                          src={order.deliveryPhoto} 
                          alt="Delivery confirmation photo" 
                          className="delivery-photo"
                          onClick={() => window.open(order.deliveryPhoto, '_blank')}
                        />
                        <p className="delivery-photo-caption">
                          {t('myOrders.photo_taken_by_driver')}
                        </p>
                      </div>
                    </div>
                  )}
                  

                  
                  <div className="order-actions-detail">
                    <button 
                      className="tracking-button"
                      onClick={(e) => openTracking(order._id, e)}
                    >
                      {t('myOrders.track_order')}
                    </button>
                    
                    {canBeCancelled(order) && (
                      <button 
                        className="cancellation-button"
                        onClick={(e) => openCancellation(order._id, e)}
                      >
                        {t('myOrders.cancel_order')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {orders.length > PAGE_SIZE && (
            <div className="pagination-row">
              <button
                className={`btn-secondary ${currentPage === 1 ? 'disabled' : ''}`}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                {t('buttons.back')}
              </button>
              <span className="page-indicator">{currentPage} / {Math.max(1, Math.ceil(orders.length / PAGE_SIZE))}</span>
              <button
                className={`btn-primary ${currentPage >= Math.ceil(orders.length / PAGE_SIZE) ? 'disabled' : ''}`}
                disabled={currentPage >= Math.ceil(orders.length / PAGE_SIZE)}
                onClick={() => setCurrentPage((p) => Math.min(Math.ceil(orders.length / PAGE_SIZE), p + 1))}
              >
                {t('buttons.next')}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Tracking Modal */}
      {trackingOrder && (
        <div className="tracking-modal-overlay">
          <OrderTracking orderId={trackingOrder} onClose={closeTracking} />
        </div>
      )}
      
      {/* Cancellation Modal */}
      {cancellingOrder && (
        <div className="cancellation-modal-overlay">
          <div className="cancellation-modal">
            <div className="cancellation-header">
              <h3>{t('myOrders.cancel_order_title')}</h3>
              <button onClick={closeCancellation} className="close-button">×</button>
            </div>
            <div className="cancellation-content">
              <p>{t('myOrders.cancel_order_confirm')}</p>
              <p className="order-id">{t('myOrders.order_number')}{cancellingOrder.substring(cancellingOrder.length - 6)}</p>
              
              <div className="cancellation-reason">
                <label htmlFor="cancellation-reason">{t('myOrders.cancellation_reason')}</label>
                <textarea 
                  id="cancellation-reason"
                  placeholder={t('myOrders.cancellation_placeholder')}
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  rows={4}
                />
              </div>
              
              <div className="cancellation-actions">
                <button 
                  className="cancel-action-button" 
                  onClick={handleCancelOrder}
                  disabled={cancellationLoading}
                >
                  {cancellationLoading ? t('myOrders.processing') : t('myOrders.confirm_cancellation')}
                </button>
                <button 
                  className="keep-order-button"
                  onClick={closeCancellation}
                  disabled={cancellationLoading}
                >
                  {t('myOrders.keep_order')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
